import { instanceBo } from "../utils/axios.create";

export interface AnalyticsProgramRef {
	id: string;
	name: string;
}

export type AnalyticsReportType =
	| "ENROLLMENT_SUMMARY"
	| "PROGRAM_ACTIVITY_SUMMARY"
	| "VISIT_DETAIL"
	| "CUSTOM";

export type AnalyticsReportFormat = "PDF" | "XLSX";

export type AnalyticsPreviewKind =
	| "SCALAR"
	| "KPI_GRID"
	| "PIE"
	| "BAR"
	| "LINE"
	| "TABLE";

export interface AnalyticsPreviewMetric {
	label: string;
	value: number | string;
}

/** One named series of a layered LINE/BAR chart. */
export interface AnalyticsPreviewSeries {
	name: string;
	points: AnalyticsPreviewMetric[];
}

/** One projected point. Mirrors the backend `SeriesPoint`. */
export interface AnalyticsProjectionPoint {
	label: string;
	value: number;
}

/** Where a projected series crosses a target value. */
export interface AnalyticsProjectionCrossing {
	target: number;
	targetLabel?: string;
	alreadyReached: boolean;
	periodsAhead?: number;
	label?: string;
	neverReason?: "FLAT" | "MOVING_AWAY" | "BEYOND_HORIZON";
}

/**
 * A deterministic extrapolation of the LINE series (SIMS-7851).
 *
 * Every number here is computed server-side by a least-squares fit — never by
 * the language model. `points` are NOT measurements and must never be summed
 * into a total or drawn so that they could be mistaken for one.
 */
export interface AnalyticsSeriesProjection {
	method: "LEAST_SQUARES_LINEAR";
	slopePerPeriod: number;
	intercept: number;
	rSquared: number;
	residualStdDev: number;
	smaWindow: number;
	movingAverage: AnalyticsProjectionPoint[];
	recentAverage: number;
	actualCount: number;
	lastActualLabel: string;
	periodUnit: "YEAR" | "MONTH" | "DAY";
	periodStep: number;
	periodName: string;
	points: AnalyticsProjectionPoint[];
	crossing?: AnalyticsProjectionCrossing;
	/** Must be shown wherever the projected numbers are shown. */
	caveats: string[];
}

/** Why no projection could be made. Surfaced rather than silently dropped. */
export interface AnalyticsProjectionUnavailable {
	refusal:
		| "TOO_FEW_POINTS"
		| "NON_NUMERIC_VALUES"
		| "UNRECOGNIZED_PERIOD_LABELS"
		| "UNEVEN_PERIODS"
		| "WEAK_FIT";
	reason: string;
}

export type AnalyticsProjection =
	| AnalyticsSeriesProjection
	| AnalyticsProjectionUnavailable;

/** Narrow a projection to the computed case. */
export const isAnalyticsProjection = (
	projection: AnalyticsProjection,
): projection is AnalyticsSeriesProjection => "method" in projection;

export interface AnalyticsReportPreview {
	kind: AnalyticsPreviewKind;
	title: string;
	value?: number | string;
	valueLabel?: string;
	// For a multi-series chart this holds the per-label TOTAL across series, so a
	// client that ignores `series` still renders a meaningful chart.
	metrics?: AnalyticsPreviewMetric[];
	/** Multi-series LINE/BAR. Present only when the agent named a series field. */
	series?: AnalyticsPreviewSeries[];
	columns?: string[];
	rows?: Record<string, string>[];
	totalRows?: number;
	/**
	 * LINE only. Deliberately separate from `metrics`/`series`: a projected point
	 * must never displace an actual one, and must never be absorbed into a total.
	 */
	projection?: AnalyticsProjection;
}

export interface AnalyticsGeneratedReport {
	id: string;
	reportType: AnalyticsReportType;
	format: AnalyticsReportFormat;
	fileName: string;
	downloadUrl: string;
	expiresAt: string;
	preview?: AnalyticsReportPreview;
}

/**
 * SIMS-7875 — where a turn stands.
 *
 * READY and AWAITING_INPUT are answers; IN_PROGRESS means the turn is still
 * being worked on and FAILED that it will not produce one. The same four values
 * the session carries server-side, so there is one vocabulary rather than a
 * parallel client-side one.
 */
export type AnalyticsTurnState =
	| "AWAITING_INPUT"
	| "IN_PROGRESS"
	| "READY"
	| "FAILED";

/** The step a turn is on, for progress while it runs. */
export type AnalyticsTurnPhase =
	| "THINKING"
	| "QUERYING"
	| "RENDERING"
	| "SUMMARIZING";

export interface AnalyticsTurnResult {
	chatSessionId: string;
	/**
	 * SIMS-7875 — this turn's id, ours when we sent one.
	 *
	 * Optional because a response from a backend that predates the field has
	 * none; the client then simply has no turn to poll for, which is the old
	 * behaviour.
	 */
	turnId?: string;
	status?: AnalyticsTurnState;
	/** Absent while IN_PROGRESS — the agent has not decided yet. */
	action?: "ask" | "generate";
	message: string;
	report?: AnalyticsGeneratedReport;
	phase?: AnalyticsTurnPhase;
}

export interface AnalyticsChatMessage {
	role: "USER" | "ASSISTANT";
	content: string;
	action?: "ask" | "generate";
	reportId?: string;
	ts: string;
}

export interface AnalyticsChatSummary {
	_id: string;
	title?: string;
	status: string;
	updatedAt?: string;
	createdAt?: string;
	reportRefs?: string[];
	/**
	 * SIMS-7875 — the turn in flight, or the last one.
	 *
	 * On the list as well as the detail, because it is how a client that lost the
	 * response to a BRAND-NEW conversation finds the session it created: the
	 * session id was allocated server-side, so the turn id is the only handle the
	 * client still holds.
	 */
	currentTurnId?: string;
	turnPhase?: AnalyticsTurnPhase;
	lastTurnError?: string;
}

export interface AnalyticsChatSession extends AnalyticsChatSummary {
	messages: AnalyticsChatMessage[];
}

export interface AnalyticsReportItem {
	_id: string;
	reportType: AnalyticsReportType;
	format?: AnalyticsReportFormat;
	fileName: string;
	status: string;
	emailStatus: string;
	narrative?: string;
	createdAt?: string;
}

/** Thumbs up/down on a generated report. A signal only — it publishes nothing. */
export type AnalyticsReportFeedback = "UP" | "DOWN";

/**
 * The type of one placeholder slot in a saved definition (SIMS-7811).
 *
 * Values are checked server-side before the substituted pipeline is re-validated;
 * the client checks the same shapes only to spare the user a round trip, never as
 * the authority.
 */
export type AnalyticsDefinitionParamType =
	| "OBJECT_ID"
	| "DATE"
	| "NUMBER"
	| "STRING";

export interface AnalyticsDefinitionParam {
	name: string;
	/** Already humanised by the backend, e.g. "Start date (from)". */
	label: string;
	type: AnalyticsDefinitionParamType;
	/**
	 * Present only for STRING/NUMBER slots. An OBJECT_ID or DATE default is
	 * deliberately never persisted server-side, so none arrives here.
	 */
	defaultValue?: string | number;
}

/**
 * Whether the definition has been put forward as a knowledge-base pattern.
 *
 * Read-only here: nomination and promotion are deliberately not in-app actions
 * (SIMS-7811), so nothing in this client writes this field.
 */
export type AnalyticsDefinitionKbStatus = "NOT_NOMINATED" | "NOMINATED";

/**
 * A saved, re-runnable report definition.
 *
 * `pipeline` is deliberately absent: the stored aggregation is the server's
 * business, the client only ever needs the name and the slots to fill. Typing it
 * would invite a component to render or edit a pipeline that only the backend
 * envelope is entitled to interpret.
 *
 * Definitions are scoped `{ accountId, userId }` server-side, so this list is
 * always only the caller's own — see SIMS-7811 on why owner-scoping is
 * load-bearing rather than incidental.
 */
export interface AnalyticsReportDefinition {
	_id: string;
	/** User-authored free text. Render as text, never as markup. */
	name: string;
	question?: string;
	sourceCollection: string;
	parameters: AnalyticsDefinitionParam[];
	kbStatus: AnalyticsDefinitionKbStatus;
	runCount: number;
	lastRunAt?: string;
	createdAt?: string;
	updatedAt?: string;
}

/**
 * SIMS-7827 (finding 1): the acting user is no longer sent by the client.
 *
 * The backend derives it from the authenticated principal (the Auth0 `sub` on
 * the API Gateway authorizer context, resolved to the internal user within the
 * account). Sending a `userId` was a spoofable identity: it scoped the chat and
 * report history, so a caller could name a colleague's id and read their
 * conversations and generated reports inside the same account.
 *
 * Chat sessions and reports are now implicitly scoped to whoever is signed in,
 * which is why no identity parameter appears below.
 */

interface SendMessageArgs {
	message: string;
	chatSessionId?: string;
	programs?: AnalyticsProgramRef[];
	/**
	 * SIMS-7875 — idempotency key generated by the caller.
	 *
	 * Sent on the first attempt and reused unchanged on a retry, so a turn the
	 * server already accepted is recognised rather than asked again. Without it a
	 * retry after the request expired would produce a second report.
	 */
	turnId?: string;
}

const sendMessage = async (
	args: SendMessageArgs,
): Promise<AnalyticsTurnResult> =>
	instanceBo
		.post("/analytics/chat", {
			message: args.message,
			chatSessionId: args.chatSessionId,
			programs: args.programs,
			turnId: args.turnId,
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * Poll one turn (SIMS-7875).
 *
 * The request that started a turn can expire before the turn finishes — API
 * Gateway stops waiting at 29s while the work carries on — so this is how the
 * client picks the answer up afterwards. It reports IN_PROGRESS with a phase
 * while the turn runs, and then the finished answer, including a freshly signed
 * download link.
 */
const getTurn = async (
	chatSessionId: string,
	turnId?: string,
): Promise<AnalyticsTurnResult> =>
	instanceBo
		.get(`/analytics/chats/${chatSessionId}/turn`, {
			params: turnId ? { turnId } : undefined,
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const listChats = async (): Promise<AnalyticsChatSummary[]> =>
	instanceBo
		.get("/analytics/chats")
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const getChat = async (chatSessionId: string): Promise<AnalyticsChatSession> =>
	instanceBo
		.get(`/analytics/chats/${chatSessionId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const deleteChat = async (
	chatSessionId: string,
): Promise<{ deleted: boolean }> =>
	instanceBo
		.delete(`/analytics/chats/${chatSessionId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const listReports = async (): Promise<AnalyticsReportItem[]> =>
	instanceBo
		.get("/analytics/reports")
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const getReportDownload = async (
	reportId: string,
): Promise<{ downloadUrl: string; expiresAt: string; fileName: string }> =>
	instanceBo
		.get(`/analytics/reports/${reportId}/download`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const emailReport = async (
	reportId: string,
	recipients: string[],
): Promise<{ sent: number; recipients: string[] }> =>
	instanceBo
		.post(`/analytics/reports/${reportId}/email`, { recipients })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * Rate a generated report (SIMS-7811).
 *
 * A curation signal and nothing more: the verdict is stored against the report,
 * it does not alter what the assistant knows and it publishes nothing to the
 * shared knowledge base. The UI copy must not promise otherwise.
 */
const rateReport = async (
	reportId: string,
	feedback: AnalyticsReportFeedback,
): Promise<{ recorded: boolean }> =>
	instanceBo
		.post(`/analytics/reports/${reportId}/feedback`, { feedback })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * Save a generated CUSTOM report as a reusable definition.
 *
 * Only labels are sent. The pipeline shape is extracted server-side from the
 * report's own stored decision params, so a client cannot author a definition the
 * envelope never validated, nor attach a pipeline to someone else's report.
 */
const saveDefinition = async (
	reportId: string,
	input: { name: string; question?: string },
): Promise<AnalyticsReportDefinition> =>
	instanceBo
		.post(`/analytics/reports/${reportId}/definitions`, {
			name: input.name,
			...(input.question ? { question: input.question } : {}),
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/** The caller's own saved definitions. Owner scoping is server-side. */
const listDefinitions = async (): Promise<AnalyticsReportDefinition[]> =>
	instanceBo
		.get("/analytics/definitions")
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * Re-run a saved definition with fresh values for its declared slots.
 *
 * Returns the same report shape as a chat-generated one, so the caller renders it
 * through exactly the same path. `values` is keyed by slot name; an undeclared key
 * is ignored server-side, so this form cannot introduce a filter the definition
 * does not declare.
 */
const runDefinition = async (
	definitionId: string,
	values: Record<string, string | number>,
	format?: AnalyticsReportFormat,
): Promise<AnalyticsGeneratedReport> =>
	instanceBo
		.post(`/analytics/definitions/${definitionId}/run`, {
			values,
			...(format ? { format } : {}),
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const deleteDefinition = async (
	definitionId: string,
): Promise<{ deleted: boolean }> =>
	instanceBo
		.delete(`/analytics/definitions/${definitionId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

export const analyticsService = {
	sendMessage,
	getTurn,
	listChats,
	getChat,
	deleteChat,
	listReports,
	getReportDownload,
	emailReport,
	rateReport,
	saveDefinition,
	listDefinitions,
	runDefinition,
	deleteDefinition,
};
